/**
 * Color-profile plumbing for HEIC → JPEG conversion. libheif hands back the photo's pixel values
 * untouched, which for an iPhone photo are Display P3. A canvas-encoded JPEG carries no profile, so
 * browsers read those values as sRGB and the photo comes out washed out.
 *
 * Tagging the JPEG with the HEIC's own profile is not enough on its own: iPhone profiles are ICC
 * v4, and imgix serves the file without applying it. So the tagged JPEG is only an intermediate
 * step that lets the browser's color management convert the pixels to sRGB, and the uploaded
 * file is plain sRGB, which renders the same everywhere.
 */
import type { SrgbJpegRequest, SrgbJpegResponse } from './srgbJpeg.worker';

interface Box {
  type: string;
  /** Offset of the box's payload, after its header. */
  start: number;
  end: number;
}

function readBoxes(view: DataView, start: number, end: number): Box[] {
  const boxes: Box[] = [];
  let offset = start;
  while (offset + 8 <= end) {
    let size = view.getUint32(offset);
    const type = String.fromCharCode(
      view.getUint8(offset + 4),
      view.getUint8(offset + 5),
      view.getUint8(offset + 6),
      view.getUint8(offset + 7),
    );
    let header = 8;
    if (size === 1) {
      size = Number(view.getBigUint64(offset + 8));
      header = 16;
    } else if (size === 0) {
      size = end - offset;
    }
    if (size < header || offset + size > end) {
      break;
    }
    boxes.push({ type, start: offset + header, end: offset + size });
    offset += size;
  }
  return boxes;
}

function findBox(boxes: Box[], type: string): Box | undefined {
  return boxes.find(box => box.type === type);
}

/**
 * Returns the ICC profile attached to the HEIF's primary image, or `undefined` when it has none
 * (or signals color with an `nclx` box instead, which we leave as it is today).
 *
 * The primary image has to be resolved through `pitm` and `ipma`: an iPhone photo also carries
 * profiles for its depth map and gain map, and those are not the photo's.
 */
export function readHeifIccProfile(buffer: ArrayBuffer): Uint8Array | undefined {
  try {
    const view = new DataView(buffer);
    const meta = findBox(readBoxes(view, 0, buffer.byteLength), 'meta');
    if (!meta) {
      return undefined;
    }
    // `meta` is a full box: skip its version and flags.
    const metaBoxes = readBoxes(view, meta.start + 4, meta.end);
    const pitm = findBox(metaBoxes, 'pitm');
    const iprp = findBox(metaBoxes, 'iprp');
    if (!pitm || !iprp) {
      return undefined;
    }
    const primaryId =
      view.getUint8(pitm.start) === 0
        ? view.getUint16(pitm.start + 4)
        : view.getUint32(pitm.start + 4);

    const iprpBoxes = readBoxes(view, iprp.start, iprp.end);
    const ipco = findBox(iprpBoxes, 'ipco');
    if (!ipco) {
      return undefined;
    }
    const properties = readBoxes(view, ipco.start, ipco.end);

    for (const ipma of iprpBoxes.filter(box => box.type === 'ipma')) {
      const version = view.getUint8(ipma.start);
      const wideIndices = (view.getUint8(ipma.start + 3) & 1) === 1;
      let offset = ipma.start + 4;
      const entryCount = view.getUint32(offset);
      offset += 4;
      for (let entry = 0; entry < entryCount; entry++) {
        const itemId = version < 1 ? view.getUint16(offset) : view.getUint32(offset);
        offset += version < 1 ? 2 : 4;
        const associationCount = view.getUint8(offset);
        offset += 1;
        for (let association = 0; association < associationCount; association++) {
          // Indices are 1-based; the top bit only marks the property as essential.
          const index = wideIndices
            ? view.getUint16(offset) & 0x7fff
            : view.getUint8(offset) & 0x7f;
          offset += wideIndices ? 2 : 1;
          const property = properties[index - 1];
          if (itemId !== primaryId || property?.type !== 'colr') {
            continue;
          }
          const colourType = String.fromCharCode(...new Uint8Array(buffer, property.start, 4));
          if (colourType === 'prof' || colourType === 'rICC') {
            return new Uint8Array(buffer.slice(property.start + 4, property.end));
          }
        }
      }
    }
    return undefined;
  } catch {
    // A malformed container just means no profile; the conversion itself still runs.
    return undefined;
  }
}

const ICC_SIGNATURE = new TextEncoder().encode('ICC_PROFILE\0');
/** A JPEG segment holds 65535 bytes, less the length field, signature and sequence bytes. */
const MAX_ICC_CHUNK = 65535 - 2 - ICC_SIGNATURE.length - 2;

/**
 * Embeds `icc` in a JPEG as APP2 `ICC_PROFILE` segments, placed after the JFIF header when there is
 * one (JFIF requires APP0 to come first).
 */
export async function embedIccProfile(jpeg: Blob, icc: Uint8Array): Promise<Blob> {
  const bytes = new Uint8Array(await jpeg.arrayBuffer());
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return jpeg;
  }
  let insertAt = 2;
  if (bytes[2] === 0xff && bytes[3] === 0xe0) {
    insertAt = 4 + ((bytes[4] << 8) | bytes[5]);
  }

  const chunkCount = Math.ceil(icc.length / MAX_ICC_CHUNK);
  const segments: Uint8Array<ArrayBuffer>[] = [];
  for (let chunk = 0; chunk < chunkCount; chunk++) {
    const data = icc.subarray(chunk * MAX_ICC_CHUNK, (chunk + 1) * MAX_ICC_CHUNK);
    const length = 2 + ICC_SIGNATURE.length + 2 + data.length;
    const segment = new Uint8Array(2 + length);
    segment.set([0xff, 0xe2, length >> 8, length & 0xff]);
    segment.set(ICC_SIGNATURE, 4);
    segment.set([chunk + 1, chunkCount], 4 + ICC_SIGNATURE.length);
    segment.set(data, 4 + ICC_SIGNATURE.length + 2);
    segments.push(segment);
  }

  return new Blob([bytes.subarray(0, insertAt), ...segments, bytes.subarray(insertAt)], {
    type: jpeg.type,
  });
}

/**
 * Re-encodes a tagged image as an sRGB JPEG, using the browser's own color management to convert
 * the pixels out of the embedded profile. The work runs in a worker for the length of one call.
 */
export function toSrgbJpeg(image: Blob, quality: number): Promise<Blob> {
  const worker = new Worker(new URL('./srgbJpeg.worker.ts', import.meta.url), { type: 'module' });
  return new Promise<Blob>((resolve, reject) => {
    worker.onmessage = ({ data }: MessageEvent<SrgbJpegResponse>) =>
      'jpeg' in data ? resolve(data.jpeg) : reject(new Error(data.error));
    worker.onerror = event => reject(new Error(event.message));
    worker.postMessage({ image, quality } satisfies SrgbJpegRequest);
  }).finally(() => worker.terminate());
}
